"use client";

import { alpha, Box, Typography, styled, Link } from "@mui/material";
import { Button } from "@/ui";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIcon from "@mui/icons-material/Phone";
import colors from "@/lib/helpers/colors";
import Reviews from "./Reviews";
import FallbackLogo from "./FallbackLogo";

const DEFAULT_BANNER_IMAGE = "/img/floats/default-banner.png";

// The "Book a meeting" button is handled separately (it shows whenever a
// calendar URL is set), so it is not part of the configurable header items.
const DEFAULT_HEADER_ITEMS = ["linkedin"];

// Fixed display order for the secondary header links, regardless of the order
// the recruiter selected them in settings.
const SECONDARY_ITEM_ORDER = ["phone", "website", "linkedin", "instagram"];

const HEADER_ITEM_CONFIG = {
  linkedin: {
    Icon: LinkedInIcon,
    label: "LinkedIn",
    href: (recruiter) => recruiter.linkedin_url,
  },
  calendar: {
    Icon: ScheduleIcon,
    label: "Book a meeting",
    href: (recruiter) => recruiter.calendar_url,
  },
  website: {
    Icon: LanguageIcon,
    label: "Our website",
    href: (recruiter) => recruiter.website,
  },
  instagram: {
    Icon: InstagramIcon,
    label: "Instagram",
    href: (recruiter) => recruiter.instagram,
  },
  phone: {
    Icon: PhoneIcon,
    label: (recruiter) => `Call ${recruiter.mobile_phone}`,
    href: (recruiter) =>
      recruiter.mobile_phone ? `tel:${recruiter.mobile_phone}` : "",
    // The MUI Phone glyph is a thin, sparse handset that doesn't fill its
    // viewBox like the filled globe/Instagram icons, so it reads smaller and
    // leaves a wider gap to its label. Render it ~1.2x larger for optical parity.
    iconSize: "16px",
  },
};

const SECONDARY_ICON_SIZE = "14.4px";

const Root = styled(Box)(({ theme }) => ({
  width: "100%",
  // Height is driven by the white recruiter box (see RecruiterProfile). The
  // banner is anchored 40px above the bottom, so Root grows/shrinks with the
  // box content.
  height: "auto",
  margin: "0 auto 36px",
  position: "relative",

  [theme.breakpoints.down("md")]: {
    height: "510px",
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%",
    height: "auto",
    // 24px gap to the float heading that follows on mobile.
    marginBottom: "24px",
  },
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  width: "100%",
  // The banner stretches to 40px above Root's bottom edge, which is the bottom
  // of the white recruiter box, so the box always hangs 40px below the banner.
  bottom: "40px",
  // overflow:hidden + its own stacking context so the decorative pills, which
  // live inside, are always clipped to the banner's (content-driven) bounds and
  // never spill past its bottom edge as the white box shrinks.
  overflow: "hidden",
  zIndex: 1,

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  // On stacked (tablet) layouts the banner is a fixed top strip rather than a
  // backdrop that tracks the white box height.
  [theme.breakpoints.down("md")]: {
    bottom: "auto",
    height: "320px",
  },

  // On phones the Float - Small recruiter card is compact, so a fixed 320px
  // banner would hang well below it and paint over the float heading that
  // follows. Track the (short) card height like the desktop layout instead,
  // keeping the card hanging its usual 40px below the banner.
  [theme.breakpoints.down("sm")]: {
    bottom: "40px",
    height: "auto",
  },
}));

// Pills are sized as a percentage of the (content-driven) banner height so both
// shrink by the same ratio and keep their relative proportions, always leaving
// their rounded ends inside the banner.
const PrimaryPill = styled(Box)(({ theme, color }) => ({
  position: "absolute",
  top: "8%",
  height: "62%",
  left: "-70px",
  width: "231px",
  borderRadius: "9999px",
  backgroundColor: alpha(color, 0.7),
  zIndex: 10,
}));

const SecondaryPill = styled(Box)(({ theme, color }) => ({
  position: "absolute",
  top: "18%",
  height: "76%",
  left: "-120px",
  width: "405px",
  borderRadius: "9999px",
  backgroundColor: alpha(color, 0.9),
  zIndex: 5,
}));

const Container = styled(Box)(({ theme }) => ({
  width: "86%",
  maxWidth: "1024px",
  margin: "44px auto 0",
  display: "flex",
  justifyContent: "space-between",

  [theme.breakpoints.down("md")]: {
    margin: "22px auto 0",
    alignItems: "flex-end",
    flexDirection: "column",
    rowGap: "18px",
  },

  // Float - Small: full-width card with 12px gutters to the screen edges,
  // matching the candidate box below.
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: "0 12px",
  },
}));

const Logo = styled(Box)(({ theme }) => ({
  width: "130px",
  height: "130px",
  borderRadius: "8px",
  backgroundColor: "white",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  zIndex: 20,
  overflow: "hidden",
  // Extra breathing room around the recruiter logo.
  padding: "4px",

  "& img": {
    width: "92%",
    height: "92%",
    objectFit: "contain",
  },

  [theme.breakpoints.down("md")]: {
    width: "100px",
    height: "100px",
  },

  // The Float - Small design drops the company logo entirely on mobile.
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const RecruiterProfile = styled(Box, {
  shouldForwardProp: (prop) => prop !== "shadowColor",
})(({ theme, shadowColor }) => ({
  width: "73%",
  // Content-driven height: grows with the recruiter details, shrinks when
  // elements (job title, company description, reviews, etc.) are absent, but
  // never below 260px.
  minHeight: "220px",
  padding: "20px 0",
  position: "relative",
  borderRadius: "8px",
  zIndex: 20,
  display: "flex",
  columnGap: "18px",
  justifyContent: "space-between",
  backgroundColor: "white",
  boxShadow: `0px 6px 0px 0px ${shadowColor}`,

  [theme.breakpoints.down("md")]: {
    width: "100%",
  },

  // Compact recruiter card to match the Float - Small proportions: auto height,
  // tight padding, rounded corners, and a subtler bottom shadow (themed color).
  [theme.breakpoints.down("sm")]: {
    height: "auto",
    minHeight: "190px",
    padding: "12px",
    borderRadius: "8px",
    columnGap: "8px",
    boxShadow: `0px 4px 0px 0px ${shadowColor}`,
  },
}));

const RecruiterDetails = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  rowGap: "12px",
  zIndex: 20,
  paddingTop: "8px",
  paddingLeft: "42px",

  [theme.breakpoints.down("sm")]: {
    minHeight: "auto",
    // Float - Small: tight vertical rhythm between name, company, position and
    // the actions block.
    rowGap: "4px",
    // 6px above the top text element (recruiter name).
    paddingTop: "6px",
    paddingLeft: "4px",
    paddingRight: "4px",
  },
}));

const IconLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "color" && prop !== "small",
})(({ color, theme, small }) => ({
  margin: small ? 0 : "0 0 6px 0",
  display: "flex",
  alignItems: "center",
  color: theme.palette.text.primary,
  fontSize: small ? "0.75em" : "inherit",

  ".MuiSvgIcon-root": {
    marginRight: "4px",
    color,
  },
}));

// Wrapper around the design-system secondary button so it sits on its own line
// above the secondary links, rendered 10% smaller via zoom.
const CalendarButtonWrap = styled(Box)(({ theme }) => ({
  marginBottom: "12px",

  "& > *": {
    zoom: 0.81,
  },

  // Float - Small: tighter horizontal padding to suit the compact mobile card,
  // plus 12px extra breathing room above the button (below the job title).
  [theme.breakpoints.down("sm")]: {
    marginTop: "12px",

    "& a, & button": {
      paddingLeft: "16px",
      paddingRight: "16px",
      // 4px shorter than the default md control height.
      height: "calc(var(--control-height) - 4px)",
      borderRadius: "var(--radius-md)",
    },
  },
}));

const SecondaryLinks = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  columnGap: "16px",
  rowGap: "4px",
  marginTop: "6px",
});

const ReviewsContainer = styled(Box)(({ theme }) => ({
  // In-flow (not absolute) so it sits below the links and contributes to the
  // content-driven box height instead of overlapping the other elements.
  marginTop: "16px",

  [theme.breakpoints.down("sm")]: {
    marginTop: "12px",
  },
}));

const RecruiterPhoto = styled(Box, {
  shouldForwardProp: (prop) => prop !== "small",
})(({ theme, small }) => ({
  width: small ? "24.4%" : "30.5%",
  height: small ? "160px" : "200px",
  borderTopLeftRadius: "146px",
  borderBottomLeftRadius: "146px",
  overflow: "hidden",

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  [theme.breakpoints.down("sm")]: {
    width: "88px",
    height: "80px",
    // Float - Small: same left-rounded shape as desktop (rounded top-left and
    // bottom-left, square on the right), scaled to the 80px photo. The square
    // right edge sits flush against the card's right edge.
    borderRadius: "40px 0 0 40px",
    position: "absolute",
    right: 0,
    top: "12px",
    bottom: "auto",
  },
}));

const JobTitle = styled(Typography)(({ theme }) => ({
  marginTop: "-4px",
  marginBottom: "8px",

  // Float - Small: job title is Inter Regular 12px in grey.
  [theme.breakpoints.down("sm")]: {
    width: "65%",
    marginTop: 0,
    marginBottom: 0,
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "normal",
    color: theme.palette.text.helper,
  },
}));

const CompanyDescription = styled(Typography)(({ theme }) => ({
  marginTop: "-2px",
  marginBottom: "8px",
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  whiteSpace: "pre-line",

  [theme.breakpoints.down("sm")]: {
    width: "65%",
  },
}));

export default function Header({ recruiter }) {
  const {
    primary_color,
    secondary_color,
    banner_image_url,
    show_bubbles,
    hide_recruiter_name,
    use_small_photo,
  } = recruiter.theme;
  const { primary, secondary } = colors({ primary_color, secondary_color });

  const selectedItems = recruiter.float_header_items?.length
    ? recruiter.float_header_items
    : DEFAULT_HEADER_ITEMS;

  // Calendar is rendered as its own button below, so exclude it from the
  // configurable inline links.
  const secondaryItems = selectedItems
    .filter((key) => key !== "calendar" && HEADER_ITEM_CONFIG[key])
    .map((key) => {
      const { Icon, label, href, iconSize } = HEADER_ITEM_CONFIG[key];
      const url = href(recruiter);
      if (!url) {
        return null;
      }
      return {
        key,
        Icon,
        url,
        label: typeof label === "function" ? label(recruiter) : label,
        external: !url.startsWith("tel:"),
        iconSize: iconSize || SECONDARY_ICON_SIZE,
      };
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        SECONDARY_ITEM_ORDER.indexOf(a.key) -
        SECONDARY_ITEM_ORDER.indexOf(b.key)
    );

  // "Book a meeting" stays full size and sits at the top. It shows whenever a
  // calendar URL is set, independent of the header item selection.
  const { Icon: CalendarIcon, label: calendarLabel } =
    HEADER_ITEM_CONFIG.calendar;
  const calendarItem = recruiter.calendar_url
    ? {
        Icon: CalendarIcon,
        url: recruiter.calendar_url,
        label: calendarLabel,
        external: true,
      }
    : null;

  return (
    <Root>
      <BannerImage>
        <img src={banner_image_url || DEFAULT_BANNER_IMAGE} alt="banner" />
        {Boolean(show_bubbles) && (
          <>
            <PrimaryPill color={primary} />
            <SecondaryPill color={secondary} />
          </>
        )}
      </BannerImage>

      <Container>
        <Logo>
          {recruiter.logo_url ? (
            <img src={recruiter.logo_url} alt={recruiter.company} />
          ) : (
            <FallbackLogo />
          )}
        </Logo>
        <RecruiterProfile shadowColor={primary}>
          <RecruiterDetails>
            <Typography
              variant="h1"
              component="h1"
              sx={(t) => ({
                mb: "6px",
                // Float - Small: name is Inter Medium 16px.
                [t.breakpoints.down("sm")]: {
                  // 4px below the recruiter name (on top of the row gap).
                  mb: "4px",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "normal",
                },
              })}
            >
              {hide_recruiter_name ? recruiter.company : recruiter.name}
            </Typography>
            {!hide_recruiter_name && (
              <Typography
                variant="h2"
                component="h2"
                sx={(t) => ({
                  // Float - Small: company is Inter Medium 12px.
                  [t.breakpoints.down("sm")]: {
                    fontFamily: "Inter, sans-serif",
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "normal",
                  },
                })}
              >
                {recruiter.company}
              </Typography>
            )}
            <JobTitle>{recruiter.job_title}</JobTitle>
            {recruiter.company_description && (
              <CompanyDescription>
                {recruiter.company_description}
              </CompanyDescription>
            )}
            <Box>
              {calendarItem && (
                <CalendarButtonWrap>
                  <Button
                    variant="secondary"
                    startIcon={<calendarItem.Icon />}
                    nativeButton={false}
                    render={
                      <a
                        href={calendarItem.url}
                        {...(calendarItem.external
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      />
                    }
                  >
                    {calendarItem.label}
                  </Button>
                </CalendarButtonWrap>
              )}
              {secondaryItems.length > 0 && (
                <SecondaryLinks>
                  {secondaryItems.map(
                    ({ key, Icon, url, label, external, iconSize }) => (
                      <IconLink
                        key={key}
                        href={url}
                        color={primary}
                        small
                        {...(external ? { target: "_blank" } : {})}
                      >
                        <Icon sx={{ fontSize: iconSize }} />
                        {label}
                      </IconLink>
                    )
                  )}
                </SecondaryLinks>
              )}
              {recruiter.reviews_widget && (
                <ReviewsContainer>
                  <Reviews reviewsWidget={recruiter.reviews_widget} />
                </ReviewsContainer>
              )}
            </Box>
          </RecruiterDetails>
          {recruiter.photo_url && (
            <RecruiterPhoto small={Boolean(use_small_photo)}>
              <img src={recruiter.photo_url} alt={recruiter.name} />
            </RecruiterPhoto>
          )}
        </RecruiterProfile>
      </Container>
    </Root>
  );
}
